import { FormField, FormStructure, SurveyForm } from '../features/surveys/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Icon } from '@iconify/react';

const TYPE_LABELS: Record<string, string> = {
  text: 'Texto',
  textarea: 'Área de texto',
  number: 'Número',
  date: 'Fecha',
  select: 'Lista desplegable',
  radio: 'Opción única (radio)',
  'checkbox-multiple': 'Selección múltiple',
  checkbox: 'Casilla sí/no',
};

function FieldReadonlyBlock({
  index,
  field,
  groupName,
}: {
  index: number;
  field: FormField;
  groupName?: string;
}) {
  const typeLabel = TYPE_LABELS[field.type] ?? field.type;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground">{index + 1}</span>
        <span className="text-sm font-medium text-foreground">{field.label || '(sin etiqueta)'}</span>
        {field.required && (
          <span className="text-xs rounded bg-primary/15 text-primary px-2 py-0.5">Obligatorio</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Tipo: <span className="text-foreground">{typeLabel}</span>
        {field.id ? (
          <>
            {' '}
            · id: <code className="text-foreground">{field.id}</code>
          </>
        ) : null}
      </p>
      {field.placeholder ? (
        <p className="text-xs text-muted-foreground">
          Placeholder: <span className="text-foreground">{field.placeholder}</span>
        </p>
      ) : null}
      {groupName ? (
        <p className="text-xs text-muted-foreground">
          Agrupación: <span className="text-foreground">{groupName}</span>
        </p>
      ) : null}
      {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox-multiple') &&
        field.options &&
        field.options.length > 0 && (
          <div className="text-xs">
            <span className="text-muted-foreground">Opciones:</span>
            <ul className="mt-1 list-disc pl-5 text-foreground">
              {field.options.map((opt, i) => (
                <li key={i}>{String(opt)}</li>
              ))}
            </ul>
          </div>
        )}
      {field.validation && Object.keys(field.validation).length > 0 && (
        <p className="text-xs text-muted-foreground">
          Validación:{' '}
          <code className="text-foreground">{JSON.stringify(field.validation)}</code>
        </p>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  form: SurveyForm | null;
  structure: FormStructure | null;
  groupNameById: Map<string, string>;
}

export function SurveyFormVersionViewDialog({ open, onClose, form, structure, groupNameById }: Props) {
  const revision = form?.revision ?? form?.version;
  const fields = structure?.fields ?? [];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[min(85vh,720px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="mdi:file-document-outline" className="w-6 h-6 text-primary shrink-0" />
            Formulario R{revision ?? '—'}
          </DialogTitle>
          <DialogDescription>
            Solo lectura: esta revisión queda fijada en el historial. Los cambios estructurales generan una nueva revisión; no se
            sobrescribe una revisión ya publicada.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay campos en esta revisión.</p>
          ) : (
            fields.map((field, idx) => (
              <FieldReadonlyBlock
                key={field.id}
                index={idx}
                field={field}
                groupName={field.groupId ? groupNameById.get(field.groupId) : undefined}
              />
            ))
          )}
        </div>
        <DialogFooter>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
